/**
 * Pure scroll maths for horizontal card rails — no DOM, so it unit-tests under the repo's plain
 * node runner. Ported from the model page's carousel in mb-new-car-frontend
 * (src/views/model/components/carouselPaging.ts), which is React and so not importable.
 */

/** Pixels of slack before an edge counts as "reached" — absorbs sub-pixel and inertial scroll. */
const EDGE_THRESHOLD = 25;

/**
 * Horizontal distance between two consecutive slide starts — i.e. the paging stride. This is the
 * slide width *plus* the inter-slide gap, so it must be measured from the slides' actual offsets
 * rather than assumed to equal the track width (which would drift by one gap per slide).
 *
 * Falls back to `fallback` (the track width) when the offsets don't yield a positive stride, which
 * covers both a single-slide track and a track measured while it is hidden (every offset is 0).
 */
export function slideStride(firstOffset: number, secondOffset: number, fallback: number): number {
    const stride = secondOffset - firstOffset;
    return stride > 0 ? stride : fallback;
}

/**
 * Which way an arrow key should page, or `null` to let the key through untouched.
 *
 * Kept pure and separate from the event handler so the four enabled/disabled combinations are
 * testable without a DOM. Returning `null` (rather than paging by zero) is what tells the caller
 * not to `preventDefault`, so an inert arrow key still scrolls the page normally.
 */
export function arrowKeyAction(key: string, canPrev: boolean, canNext: boolean): 1 | -1 | null {
    if (key === "ArrowLeft" && canPrev) return -1;
    if (key === "ArrowRight" && canNext) return 1;
    return null;
}

/** Cards a single arrow press advances, when that many are actually in view. */
const CARDS_PER_PAGE = 3;

/**
 * How far one arrow press should scroll: {@link CARDS_PER_PAGE} cards, but never more than are
 * currently visible — the arrows appear from the `sm` breakpoint up, where as few as two cards fit,
 * and advancing a fixed three there would skip one the user never sees. Always advances at least
 * one card so a press is never a no-op.
 */
export function pageDistance(stride: number, clientWidth: number): number {
    if (stride <= 0) return 0;
    const visible = Math.floor(clientWidth / stride);
    return Math.max(1, Math.min(CARDS_PER_PAGE, visible)) * stride;
}

/**
 * Whether the track can still scroll in each direction, given the current scroll metrics. Uses a
 * {@link EDGE_THRESHOLD}px slack so a near-but-not-exact end still disables the control.
 *
 * A track that is hidden (or not yet laid out) measures 0 for every metric and so reports no
 * scrolling in either direction — which is why the arrows must be re-measured once it becomes
 * visible rather than only on first render.
 */
export function edgeState(scrollLeft: number, clientWidth: number, scrollWidth: number): { canPrev: boolean; canNext: boolean } {
    return {
        canPrev: scrollLeft > EDGE_THRESHOLD,
        canNext: scrollWidth - clientWidth - scrollLeft > EDGE_THRESHOLD,
    };
}
